/**
 * Custom Before/After Image Comparison Slider
 *
 * Draggable slider with mouse, touch, and keyboard support.
 * Uses --slider-position CSS custom property to drive all visuals.
 * Supports multiple instances per page.
 */

(function () {
  'use strict';

  var SELECTORS = {
    section: '[data-before-after-section]',
    wrapper: '[data-before-after-wrapper]',
    handle: '[data-before-after-handle]',
    divider: '[data-before-after-divider]',
  };

  var CLASSES = {
    dragging: 'custom-before-after--dragging',
  };

  var KEYBOARD = {
    ArrowLeft: -1,
    ArrowRight: 1,
    Home: 'home',
    End: 'end',
  };

  var STEP = 1;
  var SHIFT_STEP = 10;

  function NSBeforeAfter(sectionEl) {
    this.section = sectionEl;
    this.wrapper = sectionEl.querySelector(SELECTORS.wrapper);
    this.handle = sectionEl.querySelector(SELECTORS.handle);
    this.divider = sectionEl.querySelector(SELECTORS.divider);

    if (!this.wrapper || !this.handle) return;

    this.isDragging = false;
    this.rafId = null;
    this.cachedRect = null;
    this.currentPosition = parseFloat(this.wrapper.style.getPropertyValue('--slider-position')) || 50;

    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onWrapperClick = this.onWrapperClick.bind(this);

    this.bindEvents();
  }

  NSBeforeAfter.prototype.bindEvents = function () {
    this.handle.addEventListener('mousedown', this.onMouseDown);
    this.handle.addEventListener('touchstart', this.onTouchStart, { passive: false });
    this.handle.addEventListener('keydown', this.onKeyDown);
    this.wrapper.addEventListener('click', this.onWrapperClick);
  };

  NSBeforeAfter.prototype.destroy = function () {
    this.handle.removeEventListener('mousedown', this.onMouseDown);
    this.handle.removeEventListener('touchstart', this.onTouchStart);
    this.handle.removeEventListener('keydown', this.onKeyDown);
    this.wrapper.removeEventListener('click', this.onWrapperClick);
    this.stopDrag();
  };

  /* ============================================
     Mouse handling
     ============================================ */

  NSBeforeAfter.prototype.onMouseDown = function (e) {
    e.preventDefault();
    this.startDrag();
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
  };

  NSBeforeAfter.prototype.onMouseMove = function (e) {
    this.updateFromClientX(e.clientX);
  };

  NSBeforeAfter.prototype.onMouseUp = function () {
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
    this.stopDrag();
  };

  /* ============================================
     Touch handling
     ============================================ */

  NSBeforeAfter.prototype.onTouchStart = function (e) {
    e.preventDefault();
    this.startDrag();
    document.addEventListener('touchmove', this.onTouchMove, { passive: false });
    document.addEventListener('touchend', this.onTouchEnd);
  };

  NSBeforeAfter.prototype.onTouchMove = function (e) {
    e.preventDefault();
    var touch = e.touches[0];
    if (touch) {
      this.updateFromClientX(touch.clientX);
    }
  };

  NSBeforeAfter.prototype.onTouchEnd = function () {
    document.removeEventListener('touchmove', this.onTouchMove);
    document.removeEventListener('touchend', this.onTouchEnd);
    this.stopDrag();
  };

  /* ============================================
     Keyboard handling
     ============================================ */

  NSBeforeAfter.prototype.onKeyDown = function (e) {
    var direction = KEYBOARD[e.key];
    if (direction === undefined) return;

    e.preventDefault();

    if (direction === 'home') {
      this.setPosition(0);
      return;
    }
    if (direction === 'end') {
      this.setPosition(100);
      return;
    }

    var step = e.shiftKey ? SHIFT_STEP : STEP;
    this.setPosition(this.currentPosition + direction * step);
  };

  /* ============================================
     Click-to-position on wrapper
     ============================================ */

  NSBeforeAfter.prototype.onWrapperClick = function (e) {
    if (e.target.closest(SELECTORS.handle)) return;
    var rect = this.wrapper.getBoundingClientRect();
    var x = e.clientX - rect.left;
    var percent = (x / rect.width) * 100;
    this.setPosition(percent);
  };

  /* ============================================
     Drag lifecycle
     ============================================ */

  NSBeforeAfter.prototype.startDrag = function () {
    this.isDragging = true;
    this.cachedRect = this.wrapper.getBoundingClientRect();
    this.section.classList.add(CLASSES.dragging);
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
  };

  NSBeforeAfter.prototype.stopDrag = function () {
    this.isDragging = false;
    this.cachedRect = null;
    this.section.classList.remove(CLASSES.dragging);
    document.body.style.userSelect = '';
    document.body.style.webkitUserSelect = '';

    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  };

  /* ============================================
     Position updates
     ============================================ */

  NSBeforeAfter.prototype.updateFromClientX = function (clientX) {
    if (!this.isDragging || !this.cachedRect) return;

    var self = this;
    if (this.rafId) return;

    this.rafId = requestAnimationFrame(function () {
      self.rafId = null;
      var rect = self.cachedRect;
      var x = clientX - rect.left;
      var percent = (x / rect.width) * 100;
      self.setPosition(percent);
    });
  };

  NSBeforeAfter.prototype.setPosition = function (percent) {
    var clamped = Math.min(100, Math.max(0, percent));
    this.currentPosition = clamped;
    this.wrapper.style.setProperty('--slider-position', clamped + '%');
    this.handle.setAttribute('aria-valuenow', Math.round(clamped));
  };

  /* ============================================
     Initialization
     ============================================ */

  function initAll() {
    var sections = document.querySelectorAll(SELECTORS.section);
    for (var i = 0; i < sections.length; i++) {
      if (!sections[i]._nsBeforeAfter) {
        sections[i]._nsBeforeAfter = new NSBeforeAfter(sections[i]);
      }
    }
  }

  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initAll);
    } else {
      initAll();
    }
  }

  init();

  // Theme editor support: reinitialize on section load
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var section = e.target.querySelector(SELECTORS.section);
      if (section) {
        if (section._nsBeforeAfter) {
          section._nsBeforeAfter.destroy();
        }
        section._nsBeforeAfter = new NSBeforeAfter(section);
      }
    });
  }
})();
